import DynamicForm, { Field } from "components/dynamic/DynamicForm";
import DynamicFilter from "components/filter/DynamicFilter";
import MobileFilterDrawer from "components/filter/MobileFilterDrawer";
import ConfirmModal from "components/modal/ConfirmModal";
import HeaderBox from "components/navbar/HeaderBox";
import DynamicTable from "components/table/DynamicTable";
import Pagination from "components/table/Pagination";
import { useModal } from "hooks/useModal";
import moment from "moment-jalaali";
import { useState } from "react";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  useCreateResponderMutation,
  useDeleteResponderMutation,
  useGetRespondersQuery,
  useLazyGetMajorsQuery,
  useUpdateResponderMutation,
} from "reduxFiles/api/userApi";
import { enumToOptions } from "services/helper";
import { Responder, ResponderGrade } from "types/assessmentTypes";
import { Column, TableAction } from "types/globalTypes";

const Responders = () => {
  // hooks
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // states
  const [item, setItem] = useState<Responder | null>(null);
  const confirmModal = useModal();
  const modal = useModal();

  // filters
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
  });

  // apis
  const { data, isFetching, refetch } = useGetRespondersQuery({
    page: pagination.page,
    limit: pagination.limit,
    filters: filters,
  });

  const [addApi, { isLoading: isAdding }] = useCreateResponderMutation();
  const [updateApi, { isLoading: isUpdating }] = useUpdateResponderMutation();
  const [deleteApi, { isLoading: isDeleting }] = useDeleteResponderMutation();

  // search major
  const [triggerMajors] = useLazyGetMajorsQuery();
  const searchMajors = async (text: string) => {
    try {
      const res = await triggerMajors({
        page: 1,
        limit: 50,
        filters: { name: text },
      }).unwrap();

      return res?.items?.map((u) => ({
        label: u.meta?.translations?.[lang] || u.name,
        value: u.id,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // form
  const fields: Field[] = [
    {
      name: "search",
      label: t("responder.search"),
      type: "text",
      filtered: true,
    },
    {
      name: "name",
      label: t("responder.name"),
      type: "text",
      formed: true,
      required: true,
    },
    {
      name: "mobile",
      label: t("user.mobile"),
      type: "mobile",
      formed: true,
      required: true,
    },
    {
      name: "nationalCode",
      label: t("responder.nationalCode"),
      type: "text",
      formed: true,
      required: true,
      filtered: true,
    },
    {
      name: "email",
      label: t("user.email"),
      type: "email",
      formed: true,
      required: true,
      filtered: true,
    },
    {
      name: "degree",
      label: t("responder.degree"),
      type: "select",
      options: enumToOptions(ResponderGrade, t, "responder.degrees"),
      formed: true,
      filtered: true,
      required: true,
    },
    {
      name: "degreesOther",
      label: t("responder.degreesOther"),
      type: "text",
      formed: true,
      required: true,
      showWhen: {
        field: "degree",
        equals: "OTHER",
      },
    },
    {
      name: "majorId",
      label: t("responder.majorId"),
      type: "select",
      onSearch: searchMajors,
      formed: true,
      required: true,
      filtered: true,
      defaultValue: {
        default: {
          label: item?.major?.meta?.translations?.[lang] || item?.major?.name,
          value: item?.major?.id,
        },
      },
      other: true,
    },
    {
      name: "majorOther",
      label: t("responder.majorOther"),
      type: "text",
      formed: true,
      required: true,
      showWhen: {
        field: "majorId",
        equals: -1,
      },
    },
    {
      name: "majorOther",
      label: t("responder.majorOther"),
      type: "select",
      options: [
        { label: t("common.yes"), value: "true" },
        { label: t("common.no"), value: "false" },
      ],
      filtered: true,
    },
    {
      name: "yearsOfExperience",
      label: t("responder.yearsOfExperience"),
      type: "number",
      formed: true,
      required: true,
    },
    {
      name: "expertise",
      label: t("responder.expertise"),
      type: "textarea",
      formed: true,
      required: true,
      rows: 3,
    },
    {
      name: "yearsOfExperienceFrom",
      label: t("responder.yearsOfExperienceFrom"),
      type: "number",
      filtered: true,
    },
    {
      name: "yearsOfExperienceTo",
      label: t("responder.yearsOfExperienceTo"),
      type: "number",
      filtered: true,
    },
    {
      name: "startDate",
      label: t("table.startDate"),
      type: "date",
      filtered: true,
    },
    {
      name: "endDate",
      label: t("table.endDate"),
      type: "date",
      filtered: true,
    },
  ];

  const columns: Column<Responder>[] = [
    {
      key: "createdAt",
      title: t("table.createdAt"),
      render: (row) =>
        moment(row.createdAt).format(
          lang === "fa" ? "jYYYY/jMM/jDD HH:mm" : "YYYY/MM/DD HH:mm"
        ),
    },
    {
      key: "name",
      title: t("responder.name"),
    },
    {
      key: "mobile",
      title: t("user.mobile"),
    },
    {
      key: "nationalCode",
      title: t("responder.nationalCode"),
    },
    {
      key: "createdBy",
      title: t("responder.createdBy"),
      render: (row) =>
        `${row.createdBy?.fullname || "-"}  (${row.createdBy?.mobile || "-"})`,
    },
  ];

  const actions: TableAction<Responder>[] = [
    {
      label: t("responder.editResponder"),
      onClick: (row) => {
        setItem(row);
        modal.openModal();
      },
      color: "secondary",
      edit: true,
    },
    {
      label: t("responder.deleteResponder"),
      delete: true,
      onClick: (row) => {
        setItem(row);
        confirmModal.openModal();
      },
      color: "error",
    },
  ];

  // functions
  const handleFilterSubmit = (filterData: FieldValues) => {
    setFilters(filterData);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination({
      page: 1,
      limit: newLimit,
    });
  };

  const handleSubmit = async (formData: FieldValues) => {
    try {
      const body = {
        ...formData,
        majorId: formData.majorId === -1 ? null : formData.majorId,
      };

      if (item) {
        await updateApi({ id: item.id, data: body }).unwrap();
      } else {
        await addApi(body).unwrap();
      }
      modal.closeModal();
      setTimeout(() => {
        setItem(null);
      }, 100);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    try {
      if (!item) return;
      await deleteApi(item.id).unwrap();
      confirmModal.closeModal();
      setTimeout(() => {
        setItem(null);
      }, 100);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <div className="mt-5 h-full">
        <HeaderBox
          title={t("responder.responderList")}
          createBtn={{
            title: t("responder.addResponder"),
            handler: modal.openModal,
          }}
          filterBtn={{
            title: t("buttons.filters"),
            handler: () => setShowFilters((prev) => !prev),
          }}
          refreshBtn={refetch}
        />

        <MobileFilterDrawer
          open={showFilters}
          onClose={() => setShowFilters((prev) => !prev)}
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          loading={isFetching}
        />
        <DynamicFilter
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          defaultValues={{}}
          loading={isFetching}
        />

        <DynamicTable<Responder>
          columns={columns}
          data={data?.items ?? []}
          actions={actions}
          loading={isFetching}
          pagination={pagination}
        />
        <Pagination
          currentPage={pagination?.page || 1}
          totalPages={data?.pageCount || 1}
          onPageChange={(newPage) => {
            setPagination((prev) => ({ ...prev, page: newPage }));
          }}
          totalItems={data?.total || 0}
          currentLimit={pagination.limit}
          onLimitChange={handleLimitChange}
        />

        <DynamicForm
          open={modal.isOpen}
          onClose={() => {
            setItem(null);
            modal.closeModal();
          }}
          onSubmit={handleSubmit}
          fields={fields?.filter((f) => f.formed)}
          defaultValues={
            item
              ? { ...item, majorId: item.majorOther ? -1 : item.major?.id }
              : {}
          }
          title={
            item ? t("responder.editResponder") : t("responder.addResponder")
          }
          isEdit={!!item}
          loading={item ? isUpdating : isAdding}
        />

        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => {
            setItem(null);
            confirmModal.closeModal();
          }}
          handler={handleDelete}
          loading={isDeleting}
          title={t("responder.deleteResponder")}
          desc={t("actions.confirmDeleteDesc", { name: item?.name || "" })}
        />
      </div>
    </div>
  );
};

export default Responders;
